import React, { useCallback, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Box, BoxSection } from 'components/_new/Box';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'components/_new/Dropdown';
import { SessionTypeEnum } from 'types/Session';
import {
	KnowledgeBaseArticle,
	KnowledgeBaseArticleContentType,
	KnowledgeBaseArticleMaterialCategory,
} from 'types/KnowledgeBase';
import { useKnowledgeBase } from 'components/knowledgeBase/knowledgeBaseContext';
import { Field } from 'components/_new/Field';
import { Button } from 'components/_new/Button';
import { knowledgeBaseApi } from 'api/knowledgeBase';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Loader } from 'components/_new/Loader';
import { Language } from 'types/Language';
import { InputText } from 'components/_new/InputText';
import { Divider } from 'components/_new/Divider';
import { FileUpload } from 'primereact/fileupload';
import { DraftEditor } from 'components/_common/DraftEditor/DraftEditor';
import { slugify } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'contexts/globalContext';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { useScope } from 'contexts/scopeContext';
import { Chips } from 'primereact/chips';
import { SlateEditor } from 'components/_new/NewSlateEditor/Editor';

type TranslatedContentForm = {
	language: string;
	title: string;
	slug: string;
	url: string;
	contentText?: string;
	contentData?: any;
	contentBlocks?: any;
	file?: File | null;
	storageUrl: null | string;
	originalFilename: null | string;
	keywords?: string[];
};

type ModifyArticleFormType = {
	contents: TranslatedContentForm[];
	contentType: KnowledgeBaseArticleContentType | null;
	directoryId: number | null;
	category: KnowledgeBaseArticleMaterialCategory | null;
	sessionType: SessionTypeEnum | null;
	sessionNumber: number | null;
	menteeAccess: boolean;
	mentorAccess: boolean;
	isProfessional: boolean;
};

type ModifyArticleFormInsideProps = {
	article?: KnowledgeBaseArticle;
	directoryId?: number | null;
	programLanguage: Language;
	optionalLanguages: Language[];
	onSave?: () => void;
};

export const ModifyArticleFormInside = ({
	article,
	directoryId,
	programLanguage,
	optionalLanguages,
	onSave,
}: ModifyArticleFormInsideProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { toastRef } = useGlobal();
	const { currentProgram: data } = useScope();
	const { directories, knowledgeBaseLinkPrefix } = useKnowledgeBase();

	const languages = [programLanguage, ...optionalLanguages];
	const articleContents = article?.contents || [];
	const contents = languages
		.map(({ code }) => {
			const currentContent = articleContents?.find(({ languageCode }) => languageCode === code);
			if (currentContent) {
				return {
					language: code,
					title: currentContent.title,
					slug: currentContent.slug,
					url: currentContent.url,
					contentText: currentContent.contentText,
					contentData: currentContent.contentData,
					contentBlocks: currentContent.contentBlocks,
					file: undefined,
					storageUrl: currentContent.storageUrl,
					originalFilename: currentContent.originalFilename,
					keywords: currentContent?.keywords ? currentContent.keywords.map(({ text }) => text) : [],
				};
			}
			if (!currentContent && code === programLanguage.code) {
				return {
					language: code,
					title: '',
					slug: '',
					url: undefined,
					contentText: undefined,
					contentData: undefined,
					contentBlocks: undefined,
					file: undefined,
					storageUrl: undefined,
					originalFilename: undefined,
					keywords: [],
				};
			}
			return null;
		})
		.filter(Boolean) as TranslatedContentForm[];

	const { control, watch, setValue, handleSubmit } = useForm<ModifyArticleFormType>({
		defaultValues: {
			contents,
			directoryId: article?.directoryId || directoryId || null,
			category: article?.category || KnowledgeBaseArticleMaterialCategory.OTHER,
			contentType: article?.contentType || KnowledgeBaseArticleContentType.HTML_WYSWIG_2,
			sessionType: article?.sessionType || null,
			sessionNumber: article?.sessionNumber || null,
			menteeAccess: article?.menteeAccess ?? true,
			mentorAccess: article?.mentorAccess ?? true,
			isProfessional: article?.isProfessional ?? false,
		},
	});

	const contentType = watch('contentType');

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'contents',
	});

	const addTranslation = (languageCode: string) => {
		const translationExists = fields.findIndex(({ language }) => language === languageCode) !== -1;
		if (!translationExists && languageCode !== programLanguage.code) {
			append({
				language: languageCode,
				title: '',
				slug: '',
				url: '',
				contentText: undefined,
				contentData: undefined,
				contentBlocks: undefined,
				storageUrl: null,
				originalFilename: null,
			});
		}
	};

	const removeTranslation = (languageCode: string) => {
		const fieldIndex = fields.findIndex(({ language }) => language === languageCode);
		if (fieldIndex !== -1 && languageCode !== programLanguage.code) {
			remove(fieldIndex);
		}
	};

	const isEditMode = Boolean(article);
	const isProgramLanguage = (languageCode: string) => {
		return programLanguage.code === languageCode;
	};

	const availableLanguages = languages.filter(({ code }) => !fields.map(({ language }) => language).includes(code));

	const { mutate: addArticleMutate, isLoading: addArticleLoading } = useMutation(
		(value: ModifyArticleFormType) => knowledgeBaseApi.program.addProgramArticle(Number(data?.id), value),
		{
			onSuccess: (data) => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('knowledgeBase.programPanel.articleForm.addeddSuccessfully'),
				});
				navigate({ pathname: `${knowledgeBaseLinkPrefix}/edit/${data.articleContent.articleId}` });
				onSave?.();
			},
		}
	);

	const { mutate: editArticleMutate, isLoading: editArticleLoading } = useMutation(
		(value: ModifyArticleFormType) =>
			knowledgeBaseApi.program.editProgramArticle(Number(data?.id), Number(article?.id), value),
		{
			onSuccess: (data) => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('knowledgeBase.programPanel.articleForm.updatedSuccessfully'),
				});
				navigate({ pathname: `${knowledgeBaseLinkPrefix}/article/${data.articleContent.articleId}` });
				onSave?.();
			},
		}
	);

	const handleSubmitForm = handleSubmit((values) => {
		if (article) {
			editArticleMutate(values);
		} else {
			addArticleMutate(values);
		}
	});
	const [fileModifyMode, setFileModifyMode] = useState(
		Object.fromEntries(contents.map(({ language, originalFilename }) => [language, !originalFilename]))
	);

	const [slateEditorChanged, setSlateEditorChanged] = useState(false);
	const triggerEditorBlur = useCallback(() => {
		if (slateEditorChanged) {
			setTimeout(() => {
				setSlateEditorChanged(false);
			}, 500);
		}
	}, [slateEditorChanged]);

	return (
		<form onSubmit={handleSubmitForm}>
			<div className="flex flex-column gap-4 sm:flex-row">
				<div className="flex-1">
					<Box variant="white-bordered" vertical>
						<BoxSection header={t('knowledgeBase.programPanel.articleForm.content')} className="w-full">
							<Controller
								control={control}
								name="contentType"
								rules={{
									required: {
										value: true,
										message: t('knowledgeBase.programPanel.articleForm.contentTypeRequired'),
									},
								}}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('knowledgeBase.programPanel.article.contentType')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputRef={ref}
											inputId={field.name}
											options={[
												{
													label: t('knowledgeBase.contentTypeOptions.pdf'),
													value: KnowledgeBaseArticleContentType.PDF,
												},
												{
													label: t('knowledgeBase.contentTypeOptions.video'),
													value: KnowledgeBaseArticleContentType.VIDEO,
												},

												{
													label: t('knowledgeBase.contentTypeOptions.html_wyswig'),
													value: KnowledgeBaseArticleContentType.HTML_WYSWIG,
												},
												{
													label: `${t(
														'knowledgeBase.contentTypeOptions.html_wyswig'
													)} (nowy)`,
													value: KnowledgeBaseArticleContentType.HTML_WYSWIG_2,
												},
												// {
												// 	label: t('knowledgeBase.contentTypeOptions.html_blocks'),
												// 	value: KnowledgeBaseArticleContentType.HTML_BLOCKS,
												// },
												{
													label: t('knowledgeBase.contentTypeOptions.file'),
													value: KnowledgeBaseArticleContentType.FILE,
												},
											]}
											disabled={isEditMode}
										/>
									</Field>
								)}
							/>
							{fields.map((field, index) => {
								const currentContent = contents.find(({ language }) => language === field.language);

								const nativeName = languages.find(({ code }) => code === field.language)?.nativeName;

								const defaullContent = currentContent?.contentText || '';
								const defaultContentData = currentContent?.contentData || [];
								const defaultContentBlocks = currentContent?.contentBlocks || '';
								const originalFilename = currentContent?.originalFilename;

								const editFile =
									fileModifyMode[field.language] !== undefined
										? fileModifyMode[field.language]
										: true;
								const toggleEditFile = () =>
									setFileModifyMode((prev) => ({ ...prev, [field.language]: !prev[field.language] }));

								return (
									<Box variant="white-bordered" key={field.id}>
										<BoxSection
											header={() => (
												<div className="flex flex-row gap-4 justify-content-between">
													<span>{`${nativeName} (${field.language})`}</span>
													{!isProgramLanguage(field.language) ? (
														<Button
															icon="trash"
															iconOnly
															label={t('actions.remove')}
															onClick={() => removeTranslation(field.language)}
															variant="danger"
															variantSize="sm"
														/>
													) : null}
												</div>
											)}
										>
											<Controller
												control={control}
												name={`contents.${index}.title`}
												rules={{
													required: { value: true, message: t('misc.forms.fieldIsRequired') },
													minLength: {
														value: 10,
														message: t('misc.forms.pleaseEnterMinChars', { min: 10 }),
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<Field
														label={t('knowledgeBase.programPanel.article.title')}
														htmlFor={`${field.name}-${index}`}
														errorMessage={error?.message}
														horizontal
													>
														<InputText
															{...field}
															id={`${field.name}-${index}`}
															onChange={(event) => {
																const { value } = event.currentTarget;
																if (value && value.length > 0) {
																	const slug = slugify(value);
																	setValue(`contents.${index}.slug`, slug);
																} else {
																	setValue(`contents.${index}.slug`, '');
																}
																field.onChange(event);
															}}
															className="w-full"
															maxLength={200}
														/>
													</Field>
												)}
											/>

											<Controller
												control={control}
												name={`contents.${index}.slug`}
												rules={{
													required: { value: true, message: t('misc.forms.fieldIsRequired') },
													minLength: {
														value: 3,
														message: t('misc.forms.pleaseEnterMinChars', { min: 3 }),
													},
												}}
												render={({ field, fieldState: { error } }) => (
													<Field
														label={t('knowledgeBase.programPanel.article.slug')}
														htmlFor={`${field.name}-${index}`}
														errorMessage={error?.message}
														horizontal
													>
														<InputText
															{...field}
															id={`${field.name}-${index}`}
															onBlur={(event) => {
																const value = slugify(event.currentTarget.value);
																field.onChange({
																	...event,
																	target: { ...event.target, value },
																	currentTarget: { ...event.currentTarget, value },
																});
																field.onBlur();
															}}
															className="w-full"
															maxLength={200}
														/>
													</Field>
												)}
											/>
										</BoxSection>

										<Divider />
										<BoxSection>
											<Controller
												control={control}
												name={`contents.${index}.keywords`}
												render={({ field: { ref, ...field }, fieldState: { error } }) => (
													<Field
														label={t('knowledgeBase.programPanel.article.keywords')}
														htmlFor={`${field.name}-${index}`}
														errorMessage={error?.message}
													>
														<Chips
															inputRef={ref}
															{...field}
															separator=","
															className="flex-1 w-full input-full"
														/>
													</Field>
												)}
											/>
										</BoxSection>

										{contentType && (
											<>
												<Divider />
												<BoxSection>
													{contentType === KnowledgeBaseArticleContentType.VIDEO && (
														<Controller
															control={control}
															name={`contents.${index}.url`}
															render={({ field, fieldState: { error } }) => (
																<Field
																	label={t('knowledgeBase.programPanel.article.url')}
																	htmlFor={`${field.name}-${index}`}
																	errorMessage={error?.message}
																>
																	<InputText
																		{...field}
																		id={`${field.name}-${index}`}
																	/>
																</Field>
															)}
															rules={{
																required: {
																	value: false,
																	message: t('misc.forms.fieldIsRequired'),
																},
															}}
														/>
													)}
													{[
														KnowledgeBaseArticleContentType.PDF,
														KnowledgeBaseArticleContentType.FILE,
													].includes(contentType) && (
														<Controller
															name={`contents.${index}.file`}
															control={control}
															rules={{
																required: {
																	value:
																		!originalFilename &&
																		[
																			KnowledgeBaseArticleContentType.PDF,
																			KnowledgeBaseArticleContentType.FILE,
																		].includes(contentType),
																	message: t('userPanel.userFiles.file.fileRequired'),
																},
															}}
															render={({
																field: { value, onChange },
																fieldState: { error },
															}) => {
																const accept = (
																	contentType === KnowledgeBaseArticleContentType.PDF
																		? ['application/pdf']
																		: [
																				'application/vnd.ms-excel',
																				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
																				'application/vnd.ms-powerpoint',
																				'application/vnd.openxmlformats-officedocument.presentationml.presentation',
																				'application/msword',
																				'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
																				'application/vnd.oasis.opendocument.presentation',
																				'application/vnd.oasis.opendocument.spreadsheet',
																				'application/vnd.oasis.opendocument.text',
																		  ]
																).join(',');
																return (
																	<Field
																		label={t(
																			'knowledgeBase.programPanel.articleForm.uploadFile'
																		)}
																		errorMessage={error?.message}
																	>
																		{originalFilename && !editFile && (
																			<div className="flex flex-row align-items-center gap-2">
																				<div className="strong my-2">
																					{originalFilename}
																				</div>
																				<Button
																					icon="pencil"
																					label={t('actions.edit')}
																					onClick={toggleEditFile}
																					iconOnly
																					variant="primary-text"
																					rounded
																				/>
																			</div>
																		)}

																		{editFile && (
																			<div className="flex flex-row align-items-center gap-2">
																				{!value && (
																					<>
																						<FileUpload
																							mode="basic"
																							accept={accept}
																							customUpload
																							onSelect={({
																								originalEvent: event,
																							}) => {
																								if (
																									((
																										event?.target as any
																									)?.files?.length ||
																										0) > 0
																								) {
																									onChange(
																										(
																											event.target as any
																										).files[0]
																									);
																								}
																							}}
																							chooseLabel={t(
																								'misc.choose'
																							)}
																						/>
																						{error && (
																							<small className="p-error font-bold ml-3">
																								{error.message}
																							</small>
																						)}
																					</>
																				)}
																				{value && (
																					<div className="flex gap-4 align-items-center justify-content-start">
																						<span>{value.name}</span>
																						{/* POPRAWIONE: Przywrócony przycisk usuwania pliku zamiast edycji */}
																						<Button
																							onClick={() => {
																								onChange(null);
																							}}
																							icon="pi pi-times"
																							className="p-button-danger p-button-xs"
																							label={t('actions.remove')}
																						/>
																					</div>
																				)}
																				{originalFilename && (
																					<Button
																						icon="times"
																						label={t('actions.cancel')}
																						onClick={toggleEditFile}
																						iconOnly
																						variant="danger-text"
																						rounded
																					/>
																				)}
																			</div>
																		)}
																	</Field>
																);
															}}
														/>
													)}
													{[
														KnowledgeBaseArticleContentType.PDF,
														KnowledgeBaseArticleContentType.FILE,
														KnowledgeBaseArticleContentType.VIDEO,
														KnowledgeBaseArticleContentType.HTML_WYSWIG,
													].includes(contentType) && (
														<Controller
															control={control}
															name={`contents.${index}.contentText`}
															render={({
																field: { ref, value },
																fieldState: { error },
															}) => (
																<div>
																	<DraftEditor
																		ref={ref}
																		defaultValue={defaullContent}
																		onChange={(value) => {
																			setValue(
																				`contents.${index}.contentText`,
																				String(value)
																			);
																		}}
																		height={20}
																		uploadRequestFunction={(file: any) =>
																			knowledgeBaseApi.program.uploadImage(
																				Number(data?.id),
																				file
																			)
																		}
																	/>
																	{error ? (
																		<div className="text-danger">
																			{error?.message}
																		</div>
																	) : null}
																</div>
															)}
															rules={{
																required: {
																	value: [
																		KnowledgeBaseArticleContentType.HTML_WYSWIG,
																	].includes(contentType),
																	message: t('misc.forms.fieldIsRequired'),
																},
																minLength: {
																	value: 10,
																	message: t('misc.forms.pleaseEnterMinChars', {
																		min: 10,
																	}),
																},
															}}
														/>
													)}

													{contentType === KnowledgeBaseArticleContentType.HTML_WYSWIG_2 && (
														<Controller
															control={control}
															name={`contents.${index}.contentData`}
															rules={{
																required: {
																	value: true,
																	message: t('misc.forms.fieldIsRequired'),
																},
															}}
															render={({
																field: { onChange, value },
																fieldState: { error },
															}) => (
																<SlateEditor
																	initialValue={defaultContentData}
																	onBlur={({ value }) => {
																		onChange(value);
																		triggerEditorBlur();
																	}}
																	onChange={() => {
																		setSlateEditorChanged(true);
																	}}
																	uploadRequestFunction={(file: any) =>
																		knowledgeBaseApi.program.uploadImage(
																			Number(data?.id),
																			file
																		)
																	}
																/>
															)}
														/>
													)}
													{contentType === KnowledgeBaseArticleContentType.HTML_BLOCKS && (
														<div>UNSUPPORTED EDITOR</div>
													)}
												</BoxSection>
											</>
										)}
									</Box>
								);
							})}
							<div className="flex flex-column gap-2">
								{availableLanguages.map(({ code, nativeName }) => (
									<Button
										key={code}
										label={t('knowledgeBase.programPanel.articleForm.addContentTranslation', {
											languageName: nativeName,
										})}
										icon="plus"
										onClick={() => addTranslation(code)}
										variant="primary-outlined"
										variantSize="sm"
									/>
								))}
							</div>
						</BoxSection>
					</Box>
				</div>

				<div className="flex flex-column gap-2 w-300-auto">
					<Box variant="white-bordered">
						<BoxSection header={t('knowledgeBase.programPanel.articleForm.availability')}>
							<div className="flex flex-column gap-1">
								<label className="text-nowrap">
									<Controller
										control={control}
										name="menteeAccess"
										render={({ field }) => (
											<Checkbox {...field} checked={field.value} className="mr-2" />
										)}
									/>
									<span>{t('knowledgeBase.programPanel.articleForm.availableForMentees')}</span>
								</label>
								<label className="text-nowrap">
									<Controller
										control={control}
										name="mentorAccess"
										render={({ field }) => (
											<Checkbox {...field} checked={field.value} className="mr-2" />
										)}
									/>
									<span>{t('knowledgeBase.programPanel.articleForm.availableForMentors')}</span>
								</label>
								<label className="text-nowrap">
									<Controller
										control={control}
										name="isProfessional"
										render={({ field }) => (
											<Checkbox {...field} checked={field.value} className="mr-2" />
										)}
									/>
									<span>{t('knowledgeBase.programPanel.articleForm.isProfessional')}</span>
								</label>
							</div>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection
							header={t('knowledgeBase.programPanel.articleForm.location')}
							contentClassName="gap-0"
						>
							<Controller
								control={control}
								name="directoryId"
								rules={{
									required: {
										value: true,
										message: t('knowledgeBase.programPanel.articleForm.directoryRequired'),
									},
								}}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('knowledgeBase.programPanel.article.directory')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											options={directories.map(({ id, name }) => ({ value: id, label: name }))}
										/>
									</Field>
								)}
							/>

							<Controller
								control={control}
								name="category"
								rules={{
									required: {
										value: true,
										message: t('knowledgeBase.programPanel.articleForm.categoryRequired'),
									},
								}}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('knowledgeBase.programPanel.article.category')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											options={[
												{
													label: t('knowledgeBase.categoryOptions.tool'),
													value: KnowledgeBaseArticleMaterialCategory.TOOL,
												},
												{
													label: t('knowledgeBase.categoryOptions.exercise'),
													value: KnowledgeBaseArticleMaterialCategory.EXERCISE,
												},
												{
													label: t('knowledgeBase.categoryOptions.knowledge'),
													value: KnowledgeBaseArticleMaterialCategory.KNOWLEDGE,
												},
												{
													label: t('knowledgeBase.categoryOptions.pdf'),
													value: KnowledgeBaseArticleMaterialCategory.PDF,
												},
												{
													label: t('knowledgeBase.categoryOptions.video'),
													value: KnowledgeBaseArticleMaterialCategory.VIDEO,
												},
												{
													label: t('knowledgeBase.categoryOptions.form'),
													value: KnowledgeBaseArticleMaterialCategory.FORM,
												},
												{
													label: t('knowledgeBase.categoryOptions.note'),
													value: KnowledgeBaseArticleMaterialCategory.NOTE,
												},
												{
													label: t('knowledgeBase.categoryOptions.other'),
													value: KnowledgeBaseArticleMaterialCategory.OTHER,
												},
											]}
										/>
									</Field>
								)}
							/>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection
							header={t('knowledgeBase.programPanel.articleForm.assignment')}
							contentClassName="gap-0"
						>
							<Controller
								control={control}
								name="sessionType"
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('knowledgeBase.programPanel.articleForm.assignmentToSessionType')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											withEmpty
											emptyLabel={t('misc.any')}
											options={[
												{
													label: t('userPanel.sessions.first.title'),
													value: SessionTypeEnum.FIRST,
												},
												{
													label: t('userPanel.sessions.second.title'),
													value: SessionTypeEnum.SECOND,
												},
												{
													label: t('userPanel.sessions.middle.title'),
													value: SessionTypeEnum.MIDDLE,
												},
												{
													label: t('userPanel.sessions.penultimate.title'),
													value: SessionTypeEnum.MIDDLE,
												},
												{
													label: t('userPanel.sessions.last.title'),
													value: SessionTypeEnum.LAST,
												},
											]}
										/>
									</Field>
								)}
							/>
							<Controller
								control={control}
								name="sessionNumber"
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('knowledgeBase.programPanel.articleForm.assignmentToSessionNumber')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											withEmpty
											emptyLabel={t('misc.any')}
											options={[
												...Array(10)
													.fill(null)
													.map((_, i) => i + 1)
													.map((sessionNumber) => ({
														label: t(`userPanel.sessions.sessionNo`, {
															no: sessionNumber,
														}),
														value: sessionNumber,
													})),
											]}
										/>
									</Field>
								)}
							/>
						</BoxSection>
					</Box>

					{/* PRZYWRÓCONE: Przycisk submit w kontenerze sidebara z className "w-full" */}
					<Button
						label={article ? t('actions.save') : t('actions.add')}
						loading={addArticleLoading || editArticleLoading}
						submit
						className="w-full"
						disabled={contentType === KnowledgeBaseArticleContentType.HTML_WYSWIG_2 && slateEditorChanged}
					/>
				</div>
			</div>
		</form>
	);
};

type ModifyArticleFormProps = { article?: KnowledgeBaseArticle; directoryId?: number | null; onSave?: () => void };

export const ModifyArticleForm = ({ article, directoryId, onSave }: ModifyArticleFormProps) => {
	const { data } = useProgramPanel();

	const {
		data: knowledgeBaseLanguages,
		isLoading: knowledgeBaseLanguagesLoading,
		isSuccess: knowledgeBaseLanguagesSuccess,
		isError: knowledgeBaseLanguagesIsError,
		error: knowledgeBaseLanguagesError,
		refetch: knowledgeBaseLanguagesRefetch,
		remove: knowledgeBaseLanguagesRemove,
	} = useQuery(
		['KNOWLEDGE_BASE_PP_DIRECTORY_LNGS', { programId: Number(data?.id) }],
		() => knowledgeBaseApi.program.getProgramAvailableLangauges(Number(data?.id)),
		{
			enabled: true,
		}
	);

	if (knowledgeBaseLanguagesIsError) {
		return <ErrorContainer action={knowledgeBaseLanguagesRefetch} error={knowledgeBaseLanguagesError} />;
	}

	if (knowledgeBaseLanguagesLoading) {
		return <Loader />;
	}

	if (knowledgeBaseLanguagesSuccess) {
		return (
			<ModifyArticleFormInside
				article={article}
				directoryId={directoryId}
				programLanguage={knowledgeBaseLanguages.program}
				optionalLanguages={knowledgeBaseLanguages.optional}
				onSave={() => {
					knowledgeBaseLanguagesRemove();
					onSave?.();
				}}
			/>
		);
	}

	return null;
};
